import { stringify } from "../../node_modules/querystring/index.esm.js";
import { formatDate } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import { amortization } from "@zillow/mortgage-calculations";
import { isWebview } from "../../node_modules/@zillow/mortgage-webview-query-params/dist/mortgage-webview-query-params.esm.esm.js";
import { range, omitBy, isNil } from "lodash";
import { useState, useEffect } from "react";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { LOAN_PROGRAMS, COMMON_AMOUNT_CONFIG, COMMON_PERCENT_CONFIG } from "../../utils/zhl-calculator.esm.js";
const RFC_2822_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEAR_OPTIONS_MIN = 1900;
const YEAR_OPTIONS_MAX = 2100;
const VALIDATE_YEAR_OPTIONS = range(YEAR_OPTIONS_MIN, YEAR_OPTIONS_MAX + 1);
const START_YEAR_OPTIONS = range(YEAR_OPTIONS_MIN, YEAR_OPTIONS_MAX + 1).map((i) => ({
  value: i.toString(),
  label: i.toString()
}));
const LOAN_TERM = {
  Fixed30Year: "Fixed30Year",
  Fixed15Year: "Fixed15Year",
  ARM5: "ARM5"
};
const TERM_TO_YEAR = {
  [LOAN_TERM.Fixed30Year]: 30,
  [LOAN_TERM.Fixed15Year]: 15,
  [LOAN_TERM.ARM5]: 30
};
const TERM_TO_RATE = {
  [LOAN_TERM.Fixed30Year]: {
    rate: 4.176
  }
};
const FieldConfig = {
  amount: {
    name: "amount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  term: {
    name: "term",
    options: [{
      value: LOAN_PROGRAMS.fixed30Year,
      label: "30-year fixed"
    }, {
      value: LOAN_PROGRAMS.fixed15Year,
      label: "15-year fixed"
    }, {
      value: LOAN_PROGRAMS.arm5,
      label: "5-year ARM"
    }],
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
            error = "Invalid loan program";
          }
          return error;
        }
      }
    }
  },
  rate: {
    name: "rate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  startMonth: {
    name: "startMonth",
    options: RFC_2822_MONTHS.map((i) => ({
      value: i,
      label: i
    })),
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (RFC_2822_MONTHS.includes(value) === false) {
            error = "Invalid start month";
          }
          return error;
        }
      }
    }
  },
  startYear: {
    name: "startYear",
    options: START_YEAR_OPTIONS,
    config: {
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (VALIDATE_YEAR_OPTIONS.includes(parseInt(value)) === false) {
            error = "Invalid start year";
          }
          return error;
        }
      }
    }
  }
};
const safe = (fn, fallback = null) => (x) => x ? fn(x) : fallback;
function useLastValidAmortizationValues(form) {
  return useLastValidValues(form, getAmortizationValues);
}
const getZillowURL = (path) => {
  const zillowOrigin = "https://www.zillow.com";
  return `${zillowOrigin}/${path.replace(/^\//, "")}`;
};
const getDateFromAmortizationMonth = (month, startMonth, startYear) => {
  const formatedStartMonth = RFC_2822_MONTHS.indexOf(startMonth) + 1;
  const jsDate = new Date(startYear, formatedStartMonth);
  jsDate.setMonth(month + jsDate.getMonth() - 1);
  return formatDate(jsDate, "M/Y");
};
function getAmortizationValues(lastValidFields) {
  const numYears = TERM_TO_YEAR[lastValidFields.term];
  const amort = amortization(lastValidFields.amount, lastValidFields.rate, numYears * 12).map((item) => ({
    ...item,
    // table date format
    formattedDate: getDateFromAmortizationMonth(item.month, lastValidFields.startMonth, lastValidFields.startYear)
  }));
  const lastItem = amort[amort.length - 1];
  const firstItem = amort[0];
  const totalInterest = (lastItem == null ? void 0 : lastItem.totalInterest) ?? 0;
  const totalPrincipal = (lastItem == null ? void 0 : lastItem.totalPrincipal) ?? 0;
  const totalPayment = totalInterest + totalPrincipal;
  const monthlyPayment = ((firstItem == null ? void 0 : firstItem.monthlyInterest) ?? 0) + ((firstItem == null ? void 0 : firstItem.monthlyPrincipal) ?? 0);
  const calculations = {
    totalInterest,
    totalPrincipal,
    totalPayment,
    amortization: amort,
    monthlyPayment
  };
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
function useCustomQuotesURL(pathBase = "mortgage-rates", params = {}) {
  const [URI, setURI] = useState(pathBase + "/");
  useEffect(() => {
    const path = isWebview() ? "mortgage/webviews/ZCQ.htm" : pathBase + "/";
    let queryString = stringify(omitBy(params, isNil));
    if (queryString) {
      queryString = `?${queryString}`;
    }
    setURI(path + queryString);
  }, [pathBase, params]);
  return getZillowURL(URI);
}
export {
  FieldConfig,
  LOAN_TERM,
  RFC_2822_MONTHS,
  START_YEAR_OPTIONS,
  TERM_TO_RATE,
  TERM_TO_YEAR,
  VALIDATE_YEAR_OPTIONS,
  getAmortizationValues,
  getZillowURL,
  safe,
  useCustomQuotesURL,
  useLastValidAmortizationValues
};
